import { useEffect, useState } from 'react';
import { useSupabaseQuery, sb } from '@/hooks/useSupabaseQuery';
import type { ActiveOption } from '@/types/domain';

/** Порог: если уникальных underlying меньше этого — считаем данные неполными. */
const MIN_UNDERLYINGS = 5;

/** Порог: если последнее обновление старше этого (мс) — считаем данные устаревшими. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 часа

export type ActiveOptionFresh = Pick<ActiveOption, 'symbol' | 'updated_at' | 'expiry'>;

/**
 * Загружает все строки из `active_options` (symbol + updated_at) с пагинацией
 * через `.range()`, обходя лимит PostgREST в 1000 строк на ответ.
 * `.limit(N)` не снимает cap `db-max-rows`, но `.range(from, to)` работает.
 */
async function fetchAllActiveOptions(): Promise<{
  data: ActiveOptionFresh[] | null;
  error: { message: string } | null;
}> {
  const PAGE = 1000;
  const all: ActiveOptionFresh[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await sb
      .from('active_options')
      .select('symbol, updated_at, expiry')
      .order('symbol', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) return { data: null, error };
    if (!data || data.length === 0) break;
    all.push(...(data as ActiveOptionFresh[]));
    if (data.length < PAGE) break; // последняя страница
    if (from >= 50_000) break; // safety stop ~50k contracts max
  }
  return { data: all, error: null };
}

interface Result {
  data: ActiveOptionFresh[] | null;
  isLoading: boolean;
  refetch: () => void;
  /** true — идёт автоматический вызов options-refresher. */
  autoRefreshing: boolean;
}

/**
 * Загружает `active_options` (symbol + updated_at + expiry) и автоматически вызывает
 * Edge Function `options-refresher`, если данные пустые или устаревшие
 * (< MIN_UNDERLYINGS уникальных underlying или последний updated_at > MAX_AGE_MS назад).
 * Данные переопрашиваются каждые 5 минут. Авто-вызов защищён флагом `autoRefreshing`.
 */
export function useEnsureActiveOptionsFresh(): Result {
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  const activeOptions = useSupabaseQuery<ActiveOptionFresh[]>(
    () => fetchAllActiveOptions(),
    [],
    5 * 60_000,
  );

  useEffect(() => {
    // Ждём завершения загрузки и не запускаем повторный рефреш, если уже идёт.
    if (activeOptions.isLoading) return;
    if (autoRefreshing) return;

    const data = activeOptions.data ?? [];

    // Считаем уникальные underlying.
    const underlyings = new Set(data.map((o) => o.symbol.split('-')[0]));
    const underlyingsCount = underlyings.size;

    // Находим максимальный updated_at.
    let maxUpdatedAt = 0;
    for (const o of data) {
      const ts = new Date(o.updated_at).getTime();
      if (!Number.isNaN(ts) && ts > maxUpdatedAt) maxUpdatedAt = ts;
    }
    const ageMs = maxUpdatedAt > 0 ? Date.now() - maxUpdatedAt : Infinity;

    const needsRefresh =
      data.length === 0 || underlyingsCount < MIN_UNDERLYINGS || ageMs > MAX_AGE_MS;

    if (!needsRefresh) return;

    // eslint-disable-next-line no-console
    console.log(
      '[useEnsureActiveOptionsFresh] auto-refresh triggered: underlyingsCount=',
      underlyingsCount,
      'ageMs=',
      ageMs,
      'dataLength=',
      data.length,
    );

    const doRefresh = async (): Promise<void> => {
      setAutoRefreshing(true);
      try {
        const { error } = await sb.functions.invoke('options-refresher');
        if (error) {
          // eslint-disable-next-line no-console
          console.error('[useEnsureActiveOptionsFresh] auto-refresh error:', error);
        } else {
          activeOptions.refetch();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[useEnsureActiveOptionsFresh] auto-refresh fetch error:', e);
      } finally {
        setAutoRefreshing(false);
      }
    };

    void doRefresh();
    // activeOptions.refetch меняется при каждом рендере — намеренно исключаем
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOptions.isLoading, activeOptions.data, autoRefreshing]);

  return {
    data: activeOptions.data,
    isLoading: activeOptions.isLoading,
    refetch: activeOptions.refetch,
    autoRefreshing,
  };
}
